import { Alert, Anchor, List, Stack, Text, Title } from "@mantine/core";
import { Link } from "@tanstack/react-router";
import classes from "./TermsAndConditions.module.css";

/**
 * Terms of Service Content Component
 */
export function TermsContent() {
  return (
    <Stack gap="xl">
      <Alert color="blue" title="Effective Date">
        February 5, 2026
      </Alert>

      <Stack gap="md" className={classes.section}>
        <Title order={3}>Introduction</Title>
        <Text>
          Welcome to Anchor, a task planning application operated by Turtle-O Tech, Ltd.
          (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;). By accessing or using Anchor,
          you agree to comply with and be bound by the following Terms of Service. If you do
          not agree with any of these terms, do not use our service.
        </Text>
        <Text size="sm" c="dimmed">
          These Terms of Service are governed by the laws of Colorado, United States.
        </Text>
      </Stack>

      <Stack gap="md" className={classes.section}>
        <Title order={3}>1. Eligibility & User Responsibilities</Title>

        <Title order={4} size="h5">1.1 Age Requirement</Title>
        <Text>
          <strong>You must be at least 18 years old to create an Anchor account.</strong> By creating
          an account, you represent and warrant that you are 18 years of age or older and have the
          legal capacity to form a binding contract.
        </Text>

        <Title order={4} size="h5" mt="sm">1.2 Responsible Use</Title>
        <Text>When using Anchor, you agree to:</Text>
        <List>
          <List.Item>Provide accurate and truthful information</List.Item>
          <List.Item>Maintain the security of your account credentials</List.Item>
          <List.Item>Use the platform in compliance with all applicable laws</List.Item>
          <List.Item>Not engage in unlawful, harmful, or disruptive activities</List.Item>
          <List.Item>Not attempt to gain unauthorized access to our systems</List.Item>
        </List>

        <Title order={4} size="h5" mt="sm">1.3 Prohibited Activities</Title>
        <Text>You agree NOT to:</Text>
        <List>
          <List.Item>Upload malicious code, viruses, or harmful software</List.Item>
          <List.Item>Harass, abuse, or harm other users</List.Item>
          <List.Item>Impersonate any person or entity</List.Item>
          <List.Item>Scrape, data mine, or extract data without authorization</List.Item>
          <List.Item>Interfere with or disrupt Anchor&apos;s functionality</List.Item>
        </List>
      </Stack>

      <Stack gap="md" className={classes.section}>
        <Title order={3}>2. Account Creation & Sub-Profiles</Title>

        <Title order={4} size="h5">2.1 Account Ownership</Title>
        <Text>
          To access Anchor, you must create an account. You are responsible for maintaining the
          confidentiality of your account credentials and for all activities that occur under your account.
        </Text>

        <Title order={4} size="h5" mt="sm">2.2 Sub-Profiles for Household Members</Title>
        <Text>
          Anchor allows you to create sub-profiles for household members, including children.
          <strong> By creating sub-profiles, you agree that:</strong>
        </Text>
        <List>
          <List.Item>You are the parent/guardian or authorized representative of any minors using sub-profiles</List.Item>
          <List.Item>You are responsible for monitoring and supervising all sub-profile activity</List.Item>
          <List.Item>You consent to data collection on behalf of sub-profile users as described in our Privacy Policy</List.Item>
          <List.Item>You are responsible for ensuring tasks assigned to minors are age-appropriate and safe</List.Item>
          <List.Item>All sub-profile data is owned and controlled by you (the primary account holder)</List.Item>
        </List>
      </Stack>

      <Stack gap="md" className={classes.section}>
        <Title order={3}>3. Privacy and Data Usage</Title>
        <Text>
          Your privacy is important to us. By using Anchor, you consent to the
          collection and use of your personal data as described in our{" "}
          <Anchor
            component={Link}
            to="/privacy"
            className={classes.link}
          >
            Privacy Policy
          </Anchor>
          .
        </Text>
      </Stack>

      <Stack gap="md" className={classes.section}>
        <Title order={3}>4. User Content & Task Suggestions</Title>

        <Title order={4} size="h5">4.1 Content Ownership</Title>
        <Text>
          You retain ownership of any content you submit to Anchor, including tasks, notes,
          and asset data. By submitting content, you grant us a limited license to store,
          display, and process it as necessary to provide Anchor&apos;s services.
        </Text>

        <Title order={4} size="h5" mt="sm">4.2 Task Suggestions & Liability Disclaimer</Title>
        <Alert color="yellow" title="Important Safety Notice">
          <Text size="sm">
            <strong>Task suggestions provided by Anchor (whether pre-defined or AI-generated in future versions)
            are for informational purposes only and should not be considered professional advice.</strong>
          </Text>
        </Alert>
        <Text mt="sm">You acknowledge and agree that:</Text>
        <List>
          <List.Item>
            <strong>You are solely responsible for assessing the safety and appropriateness of all tasks</strong>,
            especially those assigned to children or involving potential hazards.
          </List.Item>
          <List.Item>
            Anchor provides task suggestions as general guidance only and cannot account for individual
            circumstances, abilities, or safety considerations.
          </List.Item>
          <List.Item>
            You must use your own judgment and expertise when creating, assigning, or completing tasks.
          </List.Item>
          <List.Item>
            For tasks involving minors, you are responsible for ensuring age-appropriateness and
            providing adequate supervision.
          </List.Item>
          <List.Item>
            <strong>Anchor is not liable for any injuries, damages, property loss, or other consequences
            arising from task completion or suggestions.</strong>
          </List.Item>
        </List>
      </Stack>

      <Stack gap="md" className={classes.section}>
        <Title order={3}>5. Payment and Subscription Terms</Title>
        <Text>
          Currently, Anchor is offered as a free service. At this time, no
          payment or subscription fees are required to access the service.
        </Text>
        <Text>
          In the future, we may introduce paid plans for additional features or
          services. When paid plans become available, the applicable fees and
          payment terms will be clearly outlined in this section, and you will
          be notified accordingly.
        </Text>
        <Text>
          Any updates to this section will be communicated through our website
          and through your account notifications.
        </Text>
      </Stack>

      <Stack gap="md" className={classes.section}>
        <Title order={3}>6. Disclaimers and Limitation of Liability</Title>
        <Text>
          Anchor is provided &quot;as is&quot; without warranty of any kind. We
          do not guarantee that the service will be error-free or uninterrupted.
          You agree to use Anchor at your own risk.
        </Text>
        <Text>
          In no event shall we be liable for any indirect, incidental, or
          consequential damages arising from your use of Anchor.
        </Text>
      </Stack>

      <Stack gap="md" className={classes.section}>
        <Title order={3}>7. Termination</Title>
        <Text>
          We reserve the right to suspend or terminate your account if you
          violate any of these terms. You may terminate your account at any time
          by contacting our support team.
        </Text>
      </Stack>

      <Stack gap="md" className={classes.section}>
        <Title order={3}>8. Indemnification</Title>
        <Text>
          You agree to indemnify and hold harmless Anchor and its affiliates
          from any claims, damages, or liabilities arising from your use of the
          service, your violation of these terms, or infringement of any
          third-party rights.
        </Text>
      </Stack>

      <Stack gap="md" className={classes.section}>
        <Title order={3}>9. Governing Law and Dispute Resolution</Title>

        <Title order={4} size="h5">9.1 Governing Law</Title>
        <Text>
          These Terms of Service are governed by and construed in accordance with the laws of the
          State of Colorado, United States, without regard to its conflict of law provisions.
        </Text>

        <Title order={4} size="h5" mt="sm">9.2 Arbitration Agreement</Title>
        <Alert color="blue" title="Binding Arbitration">
          <Text size="sm">
            <strong>Please read this section carefully. It affects your legal rights.</strong>
          </Text>
        </Alert>
        <Text mt="sm">
          You and Turtle-O Tech, Ltd. agree that any dispute, claim, or controversy arising out of
          or relating to these Terms or Anchor (collectively, &quot;Disputes&quot;) will be resolved
          through binding arbitration, except as described below.
        </Text>

        <Title order={4} size="h5" mt="sm">9.3 Arbitration Rules</Title>
        <List>
          <List.Item>
            Arbitration will be conducted by the American Arbitration Association (AAA) under its
            Consumer Arbitration Rules.
          </List.Item>
          <List.Item>
            The arbitration will take place in Colorado or remotely via video conference.
          </List.Item>
          <List.Item>
            The arbitrator&apos;s decision will be final and binding, and judgment may be entered
            in any court of competent jurisdiction.
          </List.Item>
          <List.Item>
            You and Turtle-O Tech, Ltd. each agree to waive the right to a jury trial or to participate
            in a class action.
          </List.Item>
        </List>

        <Title order={4} size="h5" mt="sm">9.4 Small Claims Court Exception</Title>
        <Text>
          Either party may bring a Dispute in small claims court in Colorado if the Dispute qualifies
          and remains in small claims court. This exception allows individuals to pursue claims without
          the costs of arbitration.
        </Text>

        <Title order={4} size="h5" mt="sm">9.5 Injunctive Relief</Title>
        <Text>
          Either party may seek injunctive or equitable relief in state or federal courts in Colorado
          to prevent actual or threatened infringement, misappropriation, or violation of intellectual
          property rights or confidential information.
        </Text>
      </Stack>

      <Stack gap="md" className={classes.section}>
        <Title order={3}>10. Changes to the Terms</Title>
        <Text>
          We reserve the right to modify these Terms at any time. If changes are
          made, the updated terms will be posted on this page with the effective
          date. Your continued use of the service after any changes constitutes
          acceptance of the updated terms.
        </Text>
      </Stack>

      <Stack gap="md" className={classes.section}>
        <Title order={3}>11. Contact Us</Title>
        <Text>
          If you have any questions or concerns about these Terms of Service,
          please contact us:
        </Text>
        <List>
          <List.Item>
            Email:{" "}
            <Anchor href="mailto:admin@yourlifeanchor.com" className={classes.link}>
              admin@yourlifeanchor.com
            </Anchor>
          </List.Item>
          <List.Item>Company: Turtle-O Tech, Ltd.</List.Item>
          <List.Item>State: Colorado, United States</List.Item>
        </List>
        <Text mt="sm" size="sm" c="dimmed">
          We will respond to inquiries within 5-7 business days.
        </Text>
      </Stack>

    </Stack>
  );
}
